import type { JobItemDto } from "@artha/api-contracts";
import { normalizeCountryCode } from "./normalizers";

export const RESUME_ELIGIBILITY_CPA_THRESHOLDS = {
  IN: 4,
  US: 50,
} as const;

export type ResumeEligibilityCountry =
  keyof typeof RESUME_ELIGIBILITY_CPA_THRESHOLDS;

/**
 * How the resume-personalization sheet gates the apply:
 * - "none": no sheet, direct redirect.
 * - "fit": full gate — resume, review, job-fit check, OTP (high-value CPA).
 * - "collect_only": take and parse the resume, then redirect immediately —
 *   no job-fit check, no review, no OTP (remote RPH referral jobs).
 */
export type ResumeGateMode = "none" | "fit" | "collect_only";

export type ResumeEligibilityResult = {
  eligible: boolean;
  gate: ResumeGateMode;
  country: ResumeEligibilityCountry | null;
  cpaValue: number | null;
  threshold: number | null;
};

export const isRphJob = (
  job?: Partial<Pick<JobItemDto, "rph_value">> | null,
): boolean =>
  Boolean(
    job &&
      typeof job.rph_value === "number" &&
      Number.isFinite(job.rph_value) &&
      job.rph_value > 0,
  );

export const isRemoteRphJob = (
  job?: Partial<Pick<JobItemDto, "rph_value" | "work_mode">> | null,
): boolean =>
  Boolean(
    isRphJob(job) &&
      String(job?.work_mode ?? "").toLowerCase() === "remote",
  );

export const isAppcastJob = (
  job?: Partial<Pick<JobItemDto, "aggregator_name">> | null,
): boolean =>
  String(job?.aggregator_name ?? "")
    .trim()
    .toLowerCase() === "appcast";

export const evaluateResumeEligibility = (
  job: Pick<JobItemDto, "country" | "cpa_value"> &
    Partial<Pick<JobItemDto, "rph_value" | "work_mode" | "aggregator_name">>,
): ResumeEligibilityResult => {
  const normalizedCountry = normalizeCountryCode(job.country || "");
  const country = Object.prototype.hasOwnProperty.call(
    RESUME_ELIGIBILITY_CPA_THRESHOLDS,
    normalizedCountry,
  )
    ? (normalizedCountry as ResumeEligibilityCountry)
    : null;
  const cpaValue =
    typeof job.cpa_value === "number" && Number.isFinite(job.cpa_value)
      ? job.cpa_value
      : null;
  const threshold = country ? RESUME_ELIGIBILITY_CPA_THRESHOLDS[country] : null;

  // All RPH jobs require resume parsing and job-fit evaluation (same as CPA jobs,
  // without work mode restrictions or value threshold limits) — candidate is
  // redirected only if their resume matches the job.
  if (isRphJob(job)) {
    return {
      eligible: true,
      gate: "fit",
      country,
      cpaValue,
      threshold,
    };
  }

  const isAppcast = isAppcastJob(job);

  const fitGated =
    isAppcast &&
    country !== null &&
    cpaValue !== null &&
    threshold !== null &&
    cpaValue > threshold;

  return {
    eligible: fitGated,
    gate: fitGated ? "fit" : "none",
    country,
    cpaValue,
    threshold,
  };
};
